import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Space, 
  Avatar, 
  Button, 
  Form, 
  Input, 
  message, 
  Descriptions, 
  Statistic, 
  Modal,
  Row,
  Col,
  List,
  Badge,
  Divider
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  EditOutlined, 
  SaveOutlined, 
  BookOutlined, 
  LogoutOutlined, 
  CrownOutlined,
  TeamOutlined,
  FileTextOutlined,
  EyeOutlined,
  LikeOutlined
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

// 获取所有用户的统计信息（管理员专用）
const getAllUsersStats = () => {
  const stats = {
    totalUsers: 0,
    totalPosts: 0,
    totalViews: 0,
    totalLikes: 0,
    users: []
  };
  
  // Mock 用户数据
  const mockUsers = [
    { id: 1, username: 'admin', avatar: '👑', isAdmin: true },
    { id: 2, username: 'user1', avatar: '😊', isAdmin: false },
    { id: 3, username: 'test', avatar: '🌟', isAdmin: false },
  ];
  
  // 遍历 localStorage 获取每个用户的数据
  mockUsers.forEach(user => {
    const userPosts = JSON.parse(localStorage.getItem(`userPosts_${user.id}`) || '[]');
    
    let userViews = 0;
    let userLikesCount = 0;
    
    userPosts.forEach(post => {
      userViews += post.views || 0;
      userLikesCount += post.likes || 0;
    });
    
    stats.totalUsers++;
    stats.totalPosts += userPosts.length;
    stats.totalViews += userViews;
    stats.totalLikes += userLikesCount;
    
    stats.users.push({
      id: user.id,
      username: user.username,
      avatar: user.avatar,
      isAdmin: user.isAdmin,
      postCount: userPosts.length,
      views: userViews,
      likes: userLikesCount
    });
  });
  
  return stats;
};

const About = () => {
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();
  const [userInfo, setUserInfo] = useState({});
  const [postCount, setPostCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminStats, setAdminStats] = useState(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    // 从localStorage获取用户信息
    const userStr = localStorage.getItem('currentUser');
    const defaultUser = {
      username: '用户',
      email: 'user@example.com',
      avatar: '👤',
      joinDate: new Date().toISOString().split('T')[0],
      isAdmin: false
    };
    
    const currentUser = userStr ? JSON.parse(userStr) : defaultUser;
    setUserInfo(currentUser);
    setIsAdmin(currentUser.isAdmin || false);
    form.setFieldsValue(currentUser);
    
    // 获取日记数量
    const userPosts = JSON.parse(localStorage.getItem(`userPosts_${currentUser.id}`) || '[]');
    setPostCount(userPosts.length);
    
    // 如果是管理员，获取统计信息
    if (currentUser.isAdmin) {
      const stats = getAllUsersStats();
      setAdminStats(stats);
    }
  }, [form]);

  const handleSave = (values) => {
    const updatedUser = {
      ...userInfo,
      ...values,
    };
    
    localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    setUserInfo(updatedUser);
    setEditing(false);
    message.success('个人信息已更新！');
  };

  const handleLogout = () => {
    Modal.confirm({
      title: '确认退出登录',
      content: '确定要退出当前账号吗？',
      okText: '确定',
      cancelText: '取消',
      onOk() {
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('currentUser');
        message.success('已退出登录');
        navigate('/auth');
      },
    });
  };

  const handleResetUserData = (userId) => {
    if (userId === userInfo.id) {
      message.error('不能重置自己的数据！');
      return;
    }
    
    Modal.confirm({
      title: '重置用户数据',
      content: `确定要重置用户 "${userId === 1 ? 'admin' : userId === 2 ? 'user1' : 'test'}" 的日记数据吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk() {
        // 重置用户数据
        localStorage.setItem(`userPosts_${userId}`, JSON.stringify([]));
        localStorage.setItem(`userLikes_${userId}`, JSON.stringify({}));
        
        message.success('用户数据已重置！');
        
        // 刷新统计信息
        const stats = getAllUsersStats();
        setAdminStats(stats);
      },
    });
  };

  return (
    <div>
      <Title level={2} style={{ textAlign: 'center', marginBottom: 30 }}>
        个人中心 {isAdmin && <Badge count="管理员" style={{ backgroundColor: '#ff9ebb', marginLeft: 10 }} />}
      </Title>
      
      {isAdmin && adminStats && (
        <Card style={{ marginBottom: 24, background: '#fff9fb' }}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CrownOutlined style={{ color: 'gold', fontSize: 20 }} />
              <Title level={4} style={{ margin: 0 }}>管理员控制台</Title>
            </div>
            
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="总用户数"
                  value={adminStats.totalUsers}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#ff7ba9' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="总日记数"
                  value={adminStats.totalPosts}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: '#ff7ba9' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="总浏览量"
                  value={adminStats.totalViews}
                  prefix={<EyeOutlined />}
                  valueStyle={{ color: '#ff7ba9' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="总点赞数"
                  value={adminStats.totalLikes}
                  prefix={<LikeOutlined />}
                  valueStyle={{ color: '#ff7ba9' }}
                />
              </Col>
            </Row>
            
            <Divider />
            
            <Title level={5}>用户管理</Title>
            <List
              size="small"
              dataSource={adminStats.users}
              renderItem={user => (
                <List.Item
                  actions={[
                    <Button 
                      type="link" 
                      size="small" 
                      onClick={() => handleResetUserData(user.id)}
                      disabled={user.id === userInfo.id}
                    >
                      重置数据
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar 
                        style={{ 
                          backgroundColor: user.isAdmin ? '#fff9db' : '#fde3cf',
                          border: user.isAdmin ? '1px solid gold' : 'none'
                        }}
                      >
                        {user.avatar}
                      </Avatar>
                    }
                    title={
                      <Space>
                        {user.username}
                        {user.isAdmin && <CrownOutlined style={{ color: 'gold' }} />}
                        {user.id === userInfo.id && <Text type="secondary">(当前用户)</Text>}
                      </Space>
                    }
                    description={
                      <Space size={16}>
                        <Text type="secondary">日记: {user.postCount}</Text>
                        <Text type="secondary">浏览: {user.views}</Text>
                        <Text type="secondary">点赞: {user.likes}</Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Space>
        </Card>
      )}
      
      <Card>
        <Space direction="vertical" size={30} style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Avatar 
              size={120} 
              style={{ 
                backgroundColor: isAdmin ? '#fff9db' : '#ff9ebb',
                fontSize: 48,
                marginBottom: 16,
                border: isAdmin ? '2px solid gold' : 'none'
              }}
            >
              {userInfo.avatar || '👤'}
            </Avatar>
            <Title level={3}>
              {userInfo.username || '用户'}
              {isAdmin && (
                <Badge 
                  count="管理员" 
                  style={{ 
                    backgroundColor: 'gold', 
                    color: '#333',
                    marginLeft: 10,
                    fontSize: '12px',
                    fontWeight: 'normal'
                  }} 
                />
              )}
            </Title>
            <Text type="secondary">@{userInfo.username || 'user'}</Text>
          </div>
          
          <Space style={{ justifyContent: 'center', width: '100%' }}>
            <Statistic
              title="日记数量"
              value={postCount}
              prefix={<BookOutlined />}
              valueStyle={{ color: '#ff7ba9' }}
            />
          </Space>
          
          {editing ? (
            <Form
              form={form}
              onFinish={handleSave}
              layout="vertical"
              initialValues={userInfo}
            >
              <Form.Item
                name="username"
                label="用户名"
                rules={[
                  { required: true, message: '请输入用户名！' },
                  { min: 3, max: 20, message: '用户名3-20个字符！' }
                ]}
              >
                <Input 
                  prefix={<UserOutlined />}
                  placeholder="用户名"
                />
              </Form.Item>
              
              <Form.Item
                name="email"
                label="邮箱"
                rules={[
                  { required: true, message: '请输入邮箱！' },
                  { type: 'email', message: '请输入有效的邮箱地址！' }
                ]}
              >
                <Input 
                  prefix={<MailOutlined />}
                  placeholder="邮箱"
                />
              </Form.Item>
              
              <Form.Item
                name="avatar"
                label="头像符号"
                rules={[{ required: true, message: '请输入头像符号！' }]}
              >
                <Input 
                  placeholder="输入一个emoji作为头像"
                  maxLength={2}
                />
              </Form.Item>
              
              <Space style={{ width: '100%', justifyContent: 'center' }}>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                  保存
                </Button>
                <Button onClick={() => setEditing(false)}>
                  取消
                </Button>
              </Space>
            </Form>
          ) : (
            <>
              <Descriptions 
                bordered 
                column={1}
                size="middle"
                labelStyle={{ fontWeight: 'bold', width: '120px' }}
              >
                <Descriptions.Item label="用户名">
                  <Space>
                    {userInfo.username || '用户'}
                    {isAdmin && <CrownOutlined style={{ color: 'gold' }} />}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="邮箱">
                  {userInfo.email || '未设置'}
                </Descriptions.Item>
                <Descriptions.Item label="角色">
                  {isAdmin ? '管理员' : '普通用户'}
                </Descriptions.Item>
                <Descriptions.Item label="注册时间">
                  {userInfo.joinDate || new Date().toLocaleDateString()}
                </Descriptions.Item>
                <Descriptions.Item label="日记数量">
                  {postCount} 篇
                </Descriptions.Item>
              </Descriptions>
              
              <div style={{ textAlign: 'center' }}>
                <Space wrap>
                  <Button 
                    type="primary" 
                    icon={<EditOutlined />}
                    onClick={() => setEditing(true)}
                  >
                    编辑信息
                  </Button>
                  <Link to="/write">
                    <Button type="default">
                      写新日记
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button type="default">
                      {isAdmin ? '查看所有日记' : '查看我的日记'}
                    </Button>
                  </Link>
                  <Button 
                    icon={<LogoutOutlined />}
                    danger
                    onClick={handleLogout}
                  >
                    退出登录
                  </Button>
                </Space>
              </div>
            </>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default About;